import { Box, Button, Group, Stepper } from '@mantine/core'
import { UseFormReturnType, isNotEmpty, useForm } from '@mantine/form'
import { IconArrowLeft, IconArrowRight, IconCheck } from '@tabler/icons-react'
import { ComponentType, useState } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { CreatePipelineData } from '@/api/vlmApi'
import { LoadingOverlay } from '@/components/ui-shared/LoadingOverlay/LoadingOverlay'
import {
  useCreateVlmPipeline,
  useInvalidateVlmPipelines,
  useUpdateVlmPipeline
} from '@/queries/vlmQueries'
import { showToast } from '@/theme/notifications'
import {
  AlertPrompt,
  PATROL_RESERVED_TAGS,
  Pipeline,
  PipelineType,
  VlmAction
} from '@/types/vlm'
import { slugify } from '@/utils/slugify'
import { generateUUID } from '@/utils/uuid'
import {
  isAlphanumericWithSpaces,
  isAlphanumericWithSpacesHyphensUnderscores
} from '@/utils/validation/helpers'
import {
  DEFAULT_USER_PROMPT,
  MAX_ACTION_RECIPIENTS,
  MILESTONE_STATE_NAME_MAX_LENGTH,
  createDefaultAction,
  isPatrolReservedTag
} from '../vlmPipelineConfig'
import { PipelineActionsStep } from './PipelineActionsStep'
import { PipelineInputSourceStep } from './PipelineInputSourceStep'
import { PipelineNameStep } from './PipelineNameStep'
import { PipelineSettingsStep } from './PipelineSettingsStep'
import { PipelineTriggersStep } from './PipelineTriggersStep'
import { actionHasErrors } from './actionValidation'
import { DEFAULT_ALERT_CONDITION_TEMPLATES } from './alertConditionTemplates'
import { validateMaxCameras } from './cameras/validateMaxCameras'
import { PatrolScheduleStep } from './patrol/PatrolScheduleStep'
import { getPatrolSourceValidators } from './patrol/patrolValidation'
import {
  PipelineSourceFormValues,
  buildPipelineSource,
  getSourceInitialValues
} from './pipelineSourceValues'
import {
  PIPELINE_STEP_LABELS,
  PipelineStepId,
  getPipelineSteps
} from './pipelineSteps'

export const ALERT_CONDITION_NAME_MAX_LENGTH = 100
export const ALERT_CONDITION_DESCRIPTION_MAX_LENGTH = 1000

const getDefaultAlertPrompts = (): AlertPrompt[] =>
  DEFAULT_ALERT_CONDITION_TEMPLATES.map((template) => ({
    name: template.name,
    description: template.description,
    alert_tag: slugify(template.name),
    severity: 'medium'
  }))

const getDefaultActions = (): VlmAction[] => [createDefaultAction('email')]

export type AlertConditionFormValues = AlertPrompt & {
  uuid: string
}

export type ActionFormValues = VlmAction & {
  uuid: string
  // UI-only: whether this action's panel is expanded for editing (vs.
  // collapsed to a summary row). Never sent to the API.
  isEditing: boolean
}

export type PipelineFormValues = {
  name: string
  pipelineType: PipelineType | ''
  deviceId: string
  source: PipelineSourceFormValues
  userPrompt: string
  alertPrompts: AlertConditionFormValues[]
  actions: ActionFormValues[]
  enableThinking: boolean
  enableCloudResults: boolean
  fps: number
}

const validateMilestoneStateName =
  (intl: IntlShape) =>
  (value: string, values: PipelineFormValues, path: string) => {
    const index = Number(path.split('.')[1])

    if (values.actions[index].type !== 'milestone') return null

    if (!value.trim()) {
      return intl.formatMessage({
        id: 'vlm.pipelines.create.actions.milestone.state.name.required'
      })
    }

    if (value.length > MILESTONE_STATE_NAME_MAX_LENGTH) {
      return intl.formatMessage(
        { id: 'vlm.pipelines.create.actions.milestone.state.name.maxLength' },
        { max: MILESTONE_STATE_NAME_MAX_LENGTH }
      )
    }

    if (!isAlphanumericWithSpacesHyphensUnderscores(value)) {
      return intl.formatMessage({
        id: 'vlm.pipelines.create.actions.milestone.state.name.invalidChars'
      })
    }

    return null
  }

const validateMilestoneStateLevel =
  (intl: IntlShape) =>
  (value: number | '', values: PipelineFormValues, path: string) => {
    const index = Number(path.split('.')[1])

    if (values.actions[index].type !== 'milestone') return null

    if (value === '') {
      return intl.formatMessage({
        id: 'vlm.pipelines.create.actions.milestone.state.level.required'
      })
    }

    return null
  }

const normalizeEndpointPath = (value: string) =>
  value
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9/_-]/g, '')

type StepProps = { form: UseFormReturnType<PipelineFormValues> }

const STEP_COMPONENTS: Record<PipelineStepId, ComponentType<StepProps>> = {
  name: PipelineNameStep,
  source: PipelineInputSourceStep,
  schedule: PatrolScheduleStep,
  triggers: PipelineTriggersStep,
  actions: PipelineActionsStep,
  settings: PipelineSettingsStep
}

type VlmPipelineFormProps = {
  pipeline?: Pipeline
  onClose: () => void
}

export const VlmPipelineForm = ({
  pipeline,
  onClose
}: VlmPipelineFormProps) => {
  const intl = useIntl()
  const [activeStepIndex, setActiveStepIndex] = useState(0)

  const { mutateAsync: createPipeline, isPending: isCreating } =
    useCreateVlmPipeline()
  const { mutateAsync: updatePipeline, isPending: isUpdating } =
    useUpdateVlmPipeline()
  const { invalidateVlmPipelineList } = useInvalidateVlmPipelines()

  const isPending = isCreating || isUpdating

  const validateStateName = validateMilestoneStateName(intl)
  const validateStateLevel = validateMilestoneStateLevel(intl)

  const form = useForm<PipelineFormValues>({
    initialValues: {
      name: pipeline?.name ?? '',
      pipelineType: pipeline?.source.type ?? '',
      deviceId: pipeline?.device_id ?? '',
      source: getSourceInitialValues(pipeline?.source),
      userPrompt: pipeline?.user_prompt ?? DEFAULT_USER_PROMPT,
      alertPrompts: (pipeline?.alert_prompts ?? getDefaultAlertPrompts()).map(
        (alertPrompt) => ({
          ...alertPrompt,
          uuid: generateUUID()
        })
      ),
      actions: (pipeline?.actions ?? getDefaultActions()).map((action) => ({
        ...action,
        uuid: generateUUID(),
        // Actions loaded from an existing pipeline are already persisted;
        // a brand-new pipeline's default action starts in edit mode so the
        // user fills it in before it collapses.
        isEditing: !pipeline
      })),
      enableThinking: pipeline?.enable_thinking ?? false,
      enableCloudResults: pipeline?.enable_cloud_results ?? true,
      fps: pipeline?.fps ?? 2
    },
    validate: {
      name: isNotEmpty(
        intl.formatMessage({ id: 'vlm.pipelines.create.name.required' })
      ),
      pipelineType: isNotEmpty(
        intl.formatMessage({ id: 'vlm.pipelines.create.type.required' })
      ),
      deviceId: isNotEmpty(
        intl.formatMessage({ id: 'vlm.pipelines.create.device.required' })
      ),
      source: {
        watchPath: (value, values) =>
          values.pipelineType === 'directory' && !value
            ? intl.formatMessage({ id: 'vlm.pipelines.create.folder.required' })
            : null,
        endpointPath: (value, values) => {
          if (values.pipelineType !== 'http_push') return null

          if (!value) {
            return intl.formatMessage({
              id: 'vlm.pipelines.create.httpPush.required'
            })
          }

          if (!/^[a-zA-Z0-9/_-]+$/.test(value)) {
            return intl.formatMessage({
              id: 'vlm.pipelines.create.httpPush.invalid'
            })
          }

          return null
        },
        port: (value, values) =>
          values.pipelineType === 'http_push' &&
          (!value || value < 1 || value > 65535)
            ? intl.formatMessage({
                id: 'vlm.pipelines.create.httpPush.port.required'
              })
            : null,
        applicationId: (value, values) =>
          values.pipelineType === 'visionplatform' && !value
            ? intl.formatMessage({
                id: 'vlm.pipelines.create.visionplatform.required'
              })
            : null,
        milestoneServerId: (value, values) =>
          values.pipelineType === 'milestone' && !value
            ? intl.formatMessage({
                id: 'vlm.pipelines.create.milestone.required'
              })
            : null,
        triggerStates: (value, values) =>
          values.pipelineType === 'milestone' && value.length === 0
            ? intl.formatMessage({
                id: 'vlm.pipelines.create.milestone.triggerStates.required'
              })
            : null,
        milestoneCameras: (value, values) =>
          values.pipelineType === 'milestone'
            ? validateMaxCameras(intl, value)
            : null,
        ...getPatrolSourceValidators(intl)
      },
      alertPrompts: {
        name: (value, values) => {
          if (!value.trim()) {
            return intl.formatMessage({
              id: 'vlm.pipelines.create.alertConditions.name.required'
            })
          }

          if (value.length > ALERT_CONDITION_NAME_MAX_LENGTH) {
            return intl.formatMessage(
              { id: 'vlm.pipelines.create.alertConditions.name.maxLength' },
              { max: ALERT_CONDITION_NAME_MAX_LENGTH }
            )
          }

          if (!isAlphanumericWithSpaces(value)) {
            return intl.formatMessage({
              id: 'vlm.pipelines.create.alertConditions.name.invalidChars'
            })
          }

          if (isPatrolReservedTag(slugify(value))) {
            return intl.formatMessage({
              id: 'vlm.pipelines.create.alertConditions.name.reserved'
            })
          }

          const trimmedValue = value.trim().toLowerCase()

          const isDuplicate =
            values.alertPrompts.filter(
              (alertPrompt) =>
                alertPrompt.name.trim().toLowerCase() === trimmedValue
            ).length > 1

          if (isDuplicate) {
            return intl.formatMessage({
              id: 'vlm.pipelines.create.alertConditions.name.unique'
            })
          }

          return null
        },
        description: (value) => {
          if (!value.trim()) {
            return intl.formatMessage({
              id: 'vlm.pipelines.create.alertConditions.description.required'
            })
          }

          if (value.length > ALERT_CONDITION_DESCRIPTION_MAX_LENGTH) {
            return intl.formatMessage(
              {
                id: 'vlm.pipelines.create.alertConditions.description.maxLength'
              },
              { max: ALERT_CONDITION_DESCRIPTION_MAX_LENGTH }
            )
          }

          return null
        }
      },
      actions: {
        alert_tags: (value) =>
          value.length === 0
            ? intl.formatMessage({
                id: 'vlm.pipelines.create.actions.tags.required'
              })
            : null,
        recipients: (value, values, path) => {
          const index = Number(path.split('.')[1])
          if (values.actions[index].type !== 'email') return null

          if (value.length === 0) {
            return intl.formatMessage({
              id: 'vlm.pipelines.create.actions.recipients.required'
            })
          }

          if (value.length > MAX_ACTION_RECIPIENTS) {
            return intl.formatMessage(
              { id: 'vlm.pipelines.create.actions.recipients.max' },
              { max: MAX_ACTION_RECIPIENTS }
            )
          }

          return null
        },
        milestone_server_id: (value, values, path) => {
          const index = Number(path.split('.')[1])
          if (values.actions[index].type !== 'milestone') return null

          return value
            ? null
            : intl.formatMessage({
                id: 'vlm.pipelines.create.actions.milestone.server.required'
              })
        },
        states: {
          pending: {
            name: validateStateName,
            level: validateStateLevel
          },
          verified: {
            name: validateStateName,
            level: validateStateLevel
          },
          closed: {
            name: validateStateName,
            level: validateStateLevel
          }
        }
      }
    }
  })

  const steps = getPipelineSteps(form.values.pipelineType)
  const activeStep = steps[activeStepIndex]
  const isFirstStep = activeStepIndex === 0
  const isLastStep = activeStepIndex === steps.length - 1

  const validateNameStep = () => {
    const nameResult = form.validateField('name')
    const typeResult = form.validateField('pipelineType')

    if (nameResult.hasError || typeResult.hasError) return false

    // Pre-fill endpoint path from pipeline name so the user has a sensible starting point
    if (
      form.values.pipelineType === 'http_push' &&
      !form.values.source.endpointPath
    ) {
      form.setFieldValue(
        'source.endpointPath',
        normalizeEndpointPath(form.values.name)
      )
    }

    return true
  }

  const validateSourceStep = () => {
    const deviceResult = form.validateField('deviceId')
    let sourceResult = { hasError: false }

    if (form.values.pipelineType === 'directory')
      sourceResult = form.validateField('source.watchPath')

    if (form.values.pipelineType === 'http_push') {
      const endpointResult = form.validateField('source.endpointPath')
      const portResult = form.validateField('source.port')
      sourceResult = {
        hasError: endpointResult.hasError || portResult.hasError
      }
    }

    if (form.values.pipelineType === 'visionplatform')
      sourceResult = form.validateField('source.applicationId')

    if (form.values.pipelineType === 'milestone') {
      const serverResult = form.validateField('source.milestoneServerId')
      const statesResult = form.validateField('source.triggerStates')
      const camerasResult = form.validateField('source.milestoneCameras')
      sourceResult = {
        hasError:
          serverResult.hasError ||
          statesResult.hasError ||
          camerasResult.hasError
      }
    }

    if (form.values.pipelineType === 'virtual_patrol') {
      const serverResult =
        form.values.source.patrolVideoSource === 'milestone'
          ? form.validateField('source.patrolServerId')
          : { hasError: false }
      const camerasResult = form.validateField('source.patrolCameras')
      sourceResult = {
        hasError: serverResult.hasError || camerasResult.hasError
      }
    }

    return !deviceResult.hasError && !sourceResult.hasError
  }

  const validateScheduleStep = () => {
    const results = [
      form.validateField('source.patrolTimes'),
      form.validateField('source.patrolStartTime'),
      form.validateField('source.patrolTimezone'),
      form.validateField('source.patrolRoundTimeoutMinutes')
    ]

    if (results.some((result) => result.hasError)) return false

    // Duplicates were rejected above, so sorting is all that's left.
    form.setFieldValue(
      'source.patrolTimes',
      [...form.values.source.patrolTimes].sort((a, b) =>
        a.value.localeCompare(b.value)
      )
    )

    return true
  }

  const validateTriggersStep = () => {
    const alertPromptResults = form.values.alertPrompts.flatMap((_, index) => [
      form.validateField(`alertPrompts.${index}.name`),
      form.validateField(`alertPrompts.${index}.description`)
    ])

    if (alertPromptResults.some((result) => result.hasError)) return false

    // Drop action alert_tags that reference conditions removed or renamed on
    // this step, so the actions step never shows stale selections
    const validTags = new Set<string>(
      form.values.alertPrompts.map((alertPrompt) => alertPrompt.alert_tag)
    )
    // The round summary's tags aren't alert conditions; keep them on patrols.
    if (form.values.pipelineType === 'virtual_patrol')
      PATROL_RESERVED_TAGS.forEach((tag) => validTags.add(tag))

    form.values.actions.forEach((action, index) => {
      const pruned = action.alert_tags.filter((tag) => validTags.has(tag))

      if (pruned.length !== action.alert_tags.length) {
        form.setFieldValue(`actions.${index}.alert_tags`, pruned)
      }
    })

    return true
  }

  const validateActionsStep = () =>
    !form.values.actions.some((_, index) => actionHasErrors(form, index))

  const stepValidators: Record<PipelineStepId, () => boolean> = {
    name: validateNameStep,
    source: validateSourceStep,
    schedule: validateScheduleStep,
    triggers: validateTriggersStep,
    actions: validateActionsStep,
    settings: () => true
  }

  const handleNext = () => {
    if (!stepValidators[activeStep]()) return

    setActiveStepIndex((i) => Math.min(i + 1, steps.length - 1))
  }

  const handleBack = () => setActiveStepIndex((i) => Math.max(i - 1, 0))

  const handleSubmit = async () => {
    const {
      name,
      pipelineType,
      deviceId,
      source: sourceValues,
      userPrompt,
      alertPrompts,
      actions,
      enableThinking,
      enableCloudResults,
      fps
    } = form.values

    const source = buildPipelineSource(
      pipelineType as PipelineType,
      sourceValues
    )

    const payload: CreatePipelineData = {
      name,
      device_id: deviceId,
      source,
      user_prompt: userPrompt || undefined,
      alert_prompts: alertPrompts.map(
        ({ name, description, alert_tag, severity }) => ({
          name,
          description,
          alert_tag,
          severity
        })
      ),
      actions: actions.map((action) => {
        if (action.type === 'milestone') {
          return {
            type: action.type,
            alert_tags: action.alert_tags,
            milestone_server_id: action.milestone_server_id,
            states: action.states
          }
        }

        return {
          type: action.type,
          alert_tags: action.alert_tags,
          recipients: action.recipients,
          cooldown_minutes: action.cooldown_minutes
        }
      }),
      enable_thinking: enableThinking,
      enable_cloud_results: enableCloudResults,
      fps
    }

    try {
      if (pipeline) {
        await updatePipeline({ pipelineId: pipeline.id, data: payload })
      } else {
        await createPipeline(payload)
      }
      void invalidateVlmPipelineList()
      onClose()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({
            id: pipeline
              ? 'vlm.pipelines.edit.error'
              : 'vlm.pipelines.create.error'
          })
        ),
        'error'
      )
    }
  }

  const ActiveStep = STEP_COMPONENTS[activeStep]

  return (
    <Box pos="relative">
      <LoadingOverlay visible={isPending} />

      <Stepper active={activeStepIndex} mb="xl" color="green" iconSize={32}>
        {steps.map((step) => (
          <Stepper.Step
            key={step}
            label={<FormattedMessage id={PIPELINE_STEP_LABELS[step]} />}
          />
        ))}
      </Stepper>

      <ActiveStep form={form} />

      <Group justify="space-between" mt="xl">
        <Button miw={120} variant="light" color="red" onClick={onClose}>
          <FormattedMessage id="cancel" />
        </Button>

        <Group>
          {!isFirstStep && (
            <Button
              miw={120}
              variant="outline"
              leftSection={<IconArrowLeft size={16} />}
              onClick={handleBack}
            >
              <FormattedMessage id="vlm.pipelines.create.back" />
            </Button>
          )}

          {isLastStep ? (
            <Button
              miw={120}
              leftSection={<IconCheck size={16} />}
              onClick={() => void handleSubmit()}
            >
              <FormattedMessage
                id={
                  pipeline
                    ? 'vlm.pipelines.edit.confirm'
                    : 'vlm.pipelines.create.confirm'
                }
              />
            </Button>
          ) : (
            <Button
              miw={120}
              rightSection={<IconArrowRight size={16} />}
              onClick={handleNext}
            >
              <FormattedMessage id="vlm.pipelines.create.next" />
            </Button>
          )}
        </Group>
      </Group>
    </Box>
  )
}
